import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Add a contact person to a project's `contact_persons` array via PATCH.
 *
 * Props:
 *  - open, onOpenChange
 *  - projectId
 *  - currentContacts (array)
 *  - onAdded(updatedContacts)
 */
export default function AddContactDialog({
  open,
  onOpenChange,
  projectId,
  currentContacts = [],
  onAdded,
}) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", email: "", phone: "" });
  }, [open]);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const next = [
        ...currentContacts,
        { name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null },
      ];
      await api.patch(`/projects/${projectId}`, { contact_persons: next });
      toast.success("Contact added");
      onOpenChange(false);
      onAdded?.(next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border border-white/10 text-zinc-100 max-w-md"
        data-testid="add-contact-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white" style={{ fontFamily: "Outfit" }}>
            Add contact person
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Doe"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="add-contact-name"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="jane@company.com"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="add-contact-email"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Phone</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+91 98765 43210"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="add-contact-phone"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-zinc-300 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-white text-black hover:bg-zinc-200 font-medium"
            data-testid="add-contact-save"
          >
            {saving ? "Saving…" : "Add contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
